import { Fragment } from "react";

import PrintSheet, { SheetTitle, TD, TH } from "@/modules/print/sheet";
import { INCL_SUPPLY_LABEL, INCL_VAT_LABEL, snapshotVatOf, SUPPLY_LABEL, TOTAL_LABEL, VAT_LABEL, vatBasisLabel } from "@buildpy/core/modules/quote/snapshot-vat";
import { SnapshotData, SnapshotTrade } from "@buildpy/core/modules/quote/types";
import Money from "@/components/money";
import { formatRate } from "@/modules/quote/ui/format";
import { TRADE_GROUP_LABEL, TRADE_GROUPS } from "@buildpy/core/modules/template/trades";

interface Props {
  data: SnapshotData;
}

const won = (value: number) => <Money value={value} />;

const TradeRows = ({ trades }: { trades: SnapshotTrade[] }) =>
  trades.map((trade) => (
    <tr key={`${trade.trade}-${trade.sort_order}`}>
      <TD className="whitespace-nowrap">{trade.name}</TD>
      <TD className="text-right tabular-nums">{won(trade.material_amount)}</TD>
      <TD className="text-right tabular-nums">{won(trade.labor_amount)}</TD>
      <TD className="text-right font-medium tabular-nums">
        {won(trade.amount)}
      </TD>
      <TD className="text-[#525252]">{trade.condition_text}</TD>
    </tr>
  ));

/**
 * 간편견적서. 공종 단위 금액만 보여 주고 합계로 내려간다 —
 * 고객이 「얼마인가」 를 한 장에서 끝내는 자리다.
 */
const QuoteSummary = ({ data }: Props) => {
  const { quote } = data;
  const vat = snapshotVatOf(data);
  const subtotalOf = (group: (typeof TRADE_GROUPS)[number]) =>
    group === "BASIC" ? quote.subtotal_basic : quote.subtotal_style;

  const sorted = [...data.trades].sort((a, b) => a.sort_order - b.sort_order);

  return (
    <PrintSheet>
      <SheetTitle>간편 견적서</SheetTitle>

      <table className="w-full table-fixed border-collapse">
        <colgroup>
          <col className="w-[18%]" />
          <col className="w-[17%]" />
          <col className="w-[17%]" />
          <col className="w-[18%]" />
          <col />
        </colgroup>
        <thead>
          <tr>
            <TH>공종</TH>
            <TH>자재비</TH>
            <TH>노무비</TH>
            <TH>합계</TH>
            <TH>조건</TH>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr>
              <TD className="py-4 text-center text-[#737373]" colSpan={5}>
                공종이 없습니다.
              </TD>
            </tr>
          )}

          {TRADE_GROUPS.map((group) => {
            const trades = sorted.filter((t) => t.group === group);
            if (trades.length === 0) return null;
            return (
              <Fragment key={group}>
                <tr>
                  <TD className="bg-[#f5f5f5] font-semibold" colSpan={5}>
                    {TRADE_GROUP_LABEL[group]}
                  </TD>
                </tr>
                <TradeRows trades={trades} />
                <tr>
                  <TD className="bg-[#fafafa] font-semibold" colSpan={3}>
                    {TRADE_GROUP_LABEL[group]} 소계
                  </TD>
                  <TD className="bg-[#fafafa] text-right font-semibold tabular-nums">
                    {won(subtotalOf(group))}
                  </TD>
                  <TD className="bg-[#fafafa]" />
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>

      <table className="mt-4 w-full table-fixed border-collapse">
        <colgroup>
          <col />
          <col className="w-[36%]" />
        </colgroup>
        <tbody>
          <tr>
            <TD>기본 공사 소계</TD>
            <TD className="text-right tabular-nums">
              {won(quote.subtotal_basic)}
            </TD>
          </tr>
          <tr>
            <TD>스타일 공사 소계</TD>
            <TD className="text-right tabular-nums">
              {won(quote.subtotal_style)}
            </TD>
          </tr>
          <tr>
            <TD>관리비 ({formatRate(quote.mgmt_fee_rate)})</TD>
            <TD className="text-right tabular-nums">{won(quote.mgmt_fee)}</TD>
          </tr>
          <tr>
            <TD className="font-semibold">총계</TD>
            <TD className="text-right font-semibold tabular-nums">
              {won(quote.gross)}
            </TD>
          </tr>
          <tr>
            <TD>단수 정리</TD>
            <TD className="text-right tabular-nums">
              {quote.rounding_adjustment > 0
                ? <span className="whitespace-nowrap">−{won(quote.rounding_adjustment)}</span>
                : won(0)}
            </TD>
          </tr>
          {vat.mode === "EXCL" && (
            <>
              <tr>
                <TD>{SUPPLY_LABEL}</TD>
                <TD className="text-right tabular-nums">{won(vat.supply)}</TD>
              </tr>
              <tr>
                <TD>{VAT_LABEL}</TD>
                <TD className="text-right tabular-nums">{won(vat.vat)}</TD>
              </tr>
            </>
          )}
          <tr>
            <TD className="bg-[#f5f5f5] text-sm font-bold">{TOTAL_LABEL}</TD>
            <TD className="bg-[#f5f5f5] text-right text-sm font-bold tabular-nums">
              {won(vat.total)}
            </TD>
          </tr>
          {vat.mode === "INCL" && (
            <>
              <tr>
                <TD className="text-[#525252]">{INCL_SUPPLY_LABEL}</TD>
                <TD className="text-right tabular-nums text-[#525252]">{won(vat.supply)}</TD>
              </tr>
              <tr>
                <TD className="text-[#525252]">{INCL_VAT_LABEL}</TD>
                <TD className="text-right tabular-nums text-[#525252]">{won(vat.vat)}</TD>
              </tr>
            </>
          )}
        </tbody>
      </table>

      <p className="mt-2 text-xs text-[#737373]" data-testid="quote-summary-vat-basis">
        공종·항목 금액 {vatBasisLabel(vat.mode)} · 현장 여건에 따라 항목이 조정될 수 있습니다.
      </p>
    </PrintSheet>
  );
};

export default QuoteSummary;
